import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";
import gibletNoEyes from "@/assets/giblet-no-eyes.png";
import ispaiText from "@/assets/ispai-text.png";

const Index = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const { toast } = useToast();
  const { openProfileDrawer } = useProfileDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const leftEyeRef = useRef<SVGImageElement>(null);
  const rightEyeRef = useRef<SVGImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const aiOilGasButtonRef = useRef<HTMLDivElement>(null);
  
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);
  
  // Eye socket positions based on new giblet-no-eyes.png
  const leftEyeBaseX = 99;
  const leftEyeBaseY = 45;
  const rightEyeBaseX = 189;
  const rightEyeBaseY = 45;
  const eyeSize = 36;
  const pupilSize = 25;
  const maxOffset = 12;
  const movementDivisor = 18;

  useEffect(() => {
    document.body.style.zoom = "100%";
    return () => {
      document.body.style.zoom = "";
    };
  }, []);

  const handleGibletClick = () => {
    // Not logged in → Go to login page first
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Still loading profile → Wait
    if (profileLoading) {
      return;
    }
    
    // Logged in but no username → Create profile
    if (!profile?.username) {
      navigate('/create-myspai-profile');
      return;
    }
    
    // Fully set up user → Go to creators
    navigate('/creators');
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    if (!svgRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const svgCenterX = svgRect.left + svgRect.width / 2;
    const svgCenterY = svgRect.top + svgRect.height / 2;

    let targetX = mousePosition.x;
    let targetY = mousePosition.y;

    // Override with specific hover targets
    if (hoverTarget) {
      const getTargetPosition = () => {
        switch (hoverTarget) {
          case 'aiOilGas': {
            const rect = aiOilGasButtonRef.current?.getBoundingClientRect();
            return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 + 60 } : null;
          }
          default:
            return null;
        }
      };

      const targetPos = getTargetPosition();
      if (targetPos) {
        targetX = targetPos.x;
        targetY = targetPos.y;
      }
    }

    // Calculate eye positions for both eyes
    const calculateEyePosition = (eyeBaseX: number, eyeBaseY: number) => {
      const eyeCenterX = svgRect.left + eyeBaseX + eyeSize / 2;
      const eyeCenterY = svgRect.top + eyeBaseY + eyeSize / 2;
      
      const deltaX = targetX - eyeCenterX;
      const deltaY = targetY - eyeCenterY;
      const angle = Math.atan2(deltaY, deltaX);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      const movement = Math.min(distance / movementDivisor, maxOffset);
      
      let offsetX = Math.cos(angle) * movement;
      let offsetY = Math.sin(angle) * movement;
      
      // Constrain within circular bounds
      const offsetMagnitude = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
      if (offsetMagnitude > maxOffset) {
        offsetX = (offsetX / offsetMagnitude) * maxOffset;
        offsetY = (offsetY / offsetMagnitude) * maxOffset;
      }
      
      return { x: eyeBaseX + offsetX, y: eyeBaseY + offsetY };
    };

    const leftPos = calculateEyePosition(leftEyeBaseX, leftEyeBaseY);
    const rightPos = calculateEyePosition(rightEyeBaseX, rightEyeBaseY);

    // Update pupil positions (circles use cx/cy not x/y)
    leftEyeRef.current.setAttribute('cx', String(leftPos.x + eyeSize / 2));
    leftEyeRef.current.setAttribute('cy', String(leftPos.y + eyeSize / 2));
    rightEyeRef.current.setAttribute('cx', String(rightPos.x + eyeSize / 2));
    rightEyeRef.current.setAttribute('cy', String(rightPos.y + eyeSize / 2));
  }, [mousePosition, hoverTarget]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center relative">
      {/* Profile Avatar in top right when logged in */}
      {isAuthenticated && (
        <div className="absolute top-4 right-4 flex items-center gap-3">
          {profileLoading ? (
            <div className="h-16 w-16 rounded-full bg-muted animate-pulse" />
          ) : profile ? (
            <>
              <div className="text-right">
                <p className="text-sm font-semibold">{profile.display_name}</p>
                {profile.username && (
                  <button
                    onClick={() => user?.id && openProfileDrawer(user.id)}
                    className="text-xs text-primary hover:underline"
                  >
                    View Profile
                  </button>
                )}
              </div>
              <Avatar 
                className="h-16 w-16 border-2 border-primary cursor-pointer"
                onClick={() => user?.id && openProfileDrawer(user.id)}
              >
                <AvatarImage src={profile.avatar_url || undefined} alt={profile.display_name} />
                <AvatarFallback>{profile.display_name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              <button
                onClick={logout}
                className="ml-2 px-3 py-1 text-xs bg-destructive text-destructive-foreground rounded hover:opacity-80"
              >
                Logout
              </button>
            </>
          ) : null}
        </div>
      )}

      <div className="mb-8 relative flex flex-col items-center" ref={containerRef}>
        <motion.div 
          className="relative float-animation cursor-pointer" 
          initial={{ scale: 0.8, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={handleGibletClick}
        >
          <svg ref={svgRef} width="334" height="206" viewBox="0 0 334 206" className="animate-fade-in">
            {/* Base Giblet image with empty eye sockets */}
            <image href={gibletNoEyes} width="334" height="206" x="0" y="0" />
            
            {/* Left pupil */}
            <circle 
              ref={leftEyeRef as any}
              cx={leftEyeBaseX + eyeSize / 2} 
              cy={leftEyeBaseY + eyeSize / 2} 
              r={pupilSize / 2} 
              fill="black"
              style={{ transition: 'all 0.15s ease-out' }}
            />
            {/* Right pupil */}
            <circle 
              ref={rightEyeRef as any}
              cx={rightEyeBaseX + eyeSize / 2} 
              cy={rightEyeBaseY + eyeSize / 2} 
              r={pupilSize / 2} 
              fill="black"
              style={{ transition: 'all 0.15s ease-out' }}
            />
            
          </svg>
        </motion.div>
        
        <motion.img 
          src={ispaiText} 
          alt="iSPAI"
          className="w-64 mt-2 cursor-pointer"
          style={{ imageRendering: 'crisp-edges' }}
          initial={{ scale: 0.8, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={handleGibletClick}
        />
      </div>

      <div className="flex space-x-8 mb-6">
      </div>

    </div>
  );
};

export default Index;
